import { useState } from 'react';
import { AgentPageShell, FormCard, Field, inputCls } from '../../components/AgentPageShell';
import { AgentResult } from '../../components/AgentResult';
import { api, ApiError } from '../../lib/api';
import { tndStringToMillimes } from '../../lib/money';
import type { InvokeResponseBase } from '../../lib/types';

interface CacResult {
  fiscal_year: number;
  company_name: string;
  opinion: 'unqualified' | 'qualified' | 'adverse' | 'disclaimer';
  findings_count: number;
  high_severity_findings_count: number;
  ratios: Array<{ ratio_type: string; value: number; unit: string; formula: string }>;
  audit_areas: Array<{ area: string; verified: boolean; comment: string }>;
}

const OPINION_STYLE: Record<CacResult['opinion'], { label: string; cls: string }> = {
  unqualified: { label: 'Sans réserve', cls: 'bg-emerald-100 text-emerald-900 border-emerald-300' },
  qualified: { label: 'Avec réserve', cls: 'bg-amber-100 text-amber-900 border-amber-300' },
  adverse: { label: 'Défavorable', cls: 'bg-orange-100 text-orange-900 border-orange-300' },
  disclaimer: { label: 'Refus de certifier', cls: 'bg-red-100 text-red-900 border-red-300' },
};

export function CacOectPage() {
  const [fiscalYear, setFiscalYear] = useState(2026);
  const [companyName, setCompanyName] = useState('Sahel Textile Industries');
  const [sector, setSector] = useState('');
  const [revenueTotal, setRevenueTotal] = useState('100000.000');

  const [caHt, setCaHt] = useState('100000.000');
  const [achats, setAchats] = useState('60000.000');
  const [resultatNet, setResultatNet] = useState('15000.000');
  const [capitauxPropres, setCapitauxPropres] = useState('50000.000');
  const [creances, setCreances] = useState('20000.000');
  const [dettes, setDettes] = useState('10000.000');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InvokeResponseBase | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setResponse(null);
    try {
      const payload: Record<string, unknown> = {
        fiscal_year: fiscalYear,
        company_name: companyName,
        sce_entries: [],
        ttn_invoices: [],
        revenue_accounts_total_millimes: tndStringToMillimes(revenueTotal),
        financials: {
          ca_ht_millimes: tndStringToMillimes(caHt),
          achats_millimes: tndStringToMillimes(achats),
          resultat_net_millimes: tndStringToMillimes(resultatNet),
          capitaux_propres_millimes: tndStringToMillimes(capitauxPropres),
          creances_clients_millimes: tndStringToMillimes(creances),
          dettes_fournisseurs_millimes: tndStringToMillimes(dettes),
        },
      };
      if (sector) payload.sector_code = sector;
      const res = await api.invokeAgent('cac_oect', { payload, fiscal_reference_date: `${fiscalYear}-12-31` });
      setResponse(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <AgentPageShell
      title="CAC OECT"
      module="Audit · Phase 4"
      model="Sonnet 4.6"
      description={
        <>
          Pré-remplit le rapport du Commissaire aux Comptes (norme OECT) — opinion d'audit
          (sans réserve / avec réserve / défavorable / refus de certifier), calcul des ratios
          financiers de cohérence, vérification de 7 zones d'audit DGI.
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormCard title="Société auditée">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Raison sociale"><input className={inputCls} value={companyName} onChange={(e) => setCompanyName(e.target.value)} required /></Field>
            <Field label="Année fiscale"><input type="number" className={inputCls} value={fiscalYear} onChange={(e) => setFiscalYear(parseInt(e.target.value || '2026', 10))} /></Field>
            <Field label="Secteur (optionnel)">
              <select className={inputCls} value={sector} onChange={(e) => setSector(e.target.value)}>
                <option value="">Standard</option>
                <option value="industrie_export">Industrie export</option>
                <option value="banque_assurance">Banque/Assurance</option>
                <option value="hydrocarbures">Hydrocarbures</option>
              </select>
            </Field>
          </div>
        </FormCard>

        <FormCard title="États financiers (TND)">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="CA HT"><input className={inputCls + ' font-mono'} value={caHt} onChange={(e) => setCaHt(e.target.value)} required /></Field>
            <Field label="Achats consommés"><input className={inputCls + ' font-mono'} value={achats} onChange={(e) => setAchats(e.target.value)} required /></Field>
            <Field label="Résultat net"><input className={inputCls + ' font-mono'} value={resultatNet} onChange={(e) => setResultatNet(e.target.value)} required /></Field>
            <Field label="Capitaux propres"><input className={inputCls + ' font-mono'} value={capitauxPropres} onChange={(e) => setCapitauxPropres(e.target.value)} required /></Field>
            <Field label="Créances clients"><input className={inputCls + ' font-mono'} value={creances} onChange={(e) => setCreances(e.target.value)} required /></Field>
            <Field label="Dettes fournisseurs"><input className={inputCls + ' font-mono'} value={dettes} onChange={(e) => setDettes(e.target.value)} required /></Field>
            <Field label="Total comptes 70x" hint="Pour cohérence TTN ↔ recettes"><input className={inputCls + ' font-mono'} value={revenueTotal} onChange={(e) => setRevenueTotal(e.target.value)} required /></Field>
          </div>
        </FormCard>

        <button type="submit" disabled={loading} className="btn-primary">
          {loading ? 'Audit en cours…' : 'Lancer l\'audit CAC'}
        </button>
      </form>

      {error && <div className="card border border-red-300 bg-red-50 text-red-900 text-sm">{error}</div>}

      {response && (
        <AgentResult
          response={response}
          renderResult={(result) => <CacResultView result={result as CacResult} />}
        />
      )}
    </AgentPageShell>
  );
}

function CacResultView({ result }: { result: CacResult }) {
  const op = OPINION_STYLE[result.opinion];
  return (
    <div className="space-y-5">
      <section className={`p-4 rounded border-2 ${op.cls}`}>
        <div className="text-xs uppercase tracking-wider mb-1">Opinion d'audit</div>
        <div className="font-serif text-2xl font-bold">{op.label}</div>
        <div className="text-sm mt-2">
          {result.findings_count} finding{result.findings_count > 1 ? 's' : ''} ({result.high_severity_findings_count} sévérité haute)
        </div>
      </section>

      <section>
        <div className="text-xs uppercase tracking-wider text-taupe mb-2">Zones d'audit (7 axes DGI)</div>
        <div className="space-y-1">
          {result.audit_areas.map((a) => (
            <div key={a.area} className={`p-2 rounded border text-sm flex items-center gap-3 ${a.verified ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}>
              <span className="text-lg">{a.verified ? '✓' : '⚠'}</span>
              <div className="flex-1">
                <div className="font-medium">{a.area}</div>
                <div className="text-xs text-taupe">{a.comment}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {result.ratios.length > 0 && (
        <section>
          <div className="text-xs uppercase tracking-wider text-taupe mb-2">Ratios financiers</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {result.ratios.map((r) => (
              <div key={r.ratio_type} className="p-3 rounded border border-border bg-white">
                <div className="text-xs uppercase tracking-wider text-taupe">{prettyRatio(r.ratio_type)}</div>
                <div className="font-mono text-lg">
                  {r.value.toFixed(2)} <span className="text-xs text-taupe">{r.unit === 'days' ? 'j' : r.unit === 'pct' ? '%' : ''}</span>
                </div>
                <div className="text-xs text-taupe italic mt-0.5">{r.formula}</div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function prettyRatio(t: string): string {
  return ({ dso: 'DSO (jours)', dpo: 'DPO (jours)', marge_brute: 'Marge brute', marge_nette: 'Marge nette', roe: 'ROE', prorata_tva: 'Prorata TVA' } as Record<string, string>)[t] ?? t;
}
